import axios from 'axios';
const SERVER_URL = process.env.REACT_APP_API_URL || 'http://localhost:1337';

export interface Lesson {
  id: number;
  documentId: string;
  title: string;
  content: string;
  order: number;
  duration?: number;
  videoUrl?: string;
  isFree: boolean;
  slug: string;
  createdAt: string;
  updatedAt: string;
  publishedAt?: string;
  locale?: string;
}

export interface LessonFragmentImage {
  id: number;
  documentId: string;
  name: string;
  alternativeText?: string;
  caption?: string;
  width: number;
  height: number;
  formats: {
    thumbnail: {
      name: string;
      hash: string;
      ext: string;
      mime: string;
      width: number;
      height: number;
      size: number;
      sizeInBytes: number;
      url: string;
    };
    small: {
      name: string;
      hash: string;
      ext: string;
      mime: string;
      width: number;
      height: number;
      size: number;
      sizeInBytes: number;
      url: string;
    };
    medium: {
      name: string;
      hash: string;
      ext: string;
      mime: string;
      width: number;
      height: number;
      size: number;
      sizeInBytes: number;
      url: string;
    };
    large: {
      name: string;
      hash: string;
      ext: string;
      mime: string;
      width: number;
      height: number;
      size: number;
      sizeInBytes: number;
      url: string;
    };
  };
  hash: string;
  ext: string;
  mime: string;
  size: number;
  url: string;
  previewUrl?: string;
  provider: string;
  provider_metadata?: any;
  folderPath: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  locale?: string;
}

export interface LessonFragment {
  __component: string;
  id: number;
  title: string;
  content: string;
  order: number;
  fragmentType: string;
  isInteractive: boolean;
  estimatedDuration?: number;
  image?: LessonFragmentImage;
  practices?: Practice[];
}

export interface Practice {
  order: number;
  id: number;
  title: string;
  description: string | null;
  practiceType: 'PROMPT_CREATION' | 'SINGLE_CHOICE_TEST';
  isRequired: boolean;
  promptCreation?: PromptCreationPractice;
  singleChoiceTest?: SingleChoiceTestPractice;
}

export interface PromptCreationPractice {
  id: number;
  title: string;
  description: string;
  fullDescription: string;
  examplePrompt: string;
  successMessage: string;
  tip: string;
  wrongMessage: string;
}

export interface SingleChoiceTestPractice {
  id: number;
  question: string;
  hint: string;
  correctChoiceMessage: string;
  wrongChoiceMessage: string;
  explanation: string | null;
  options: SingleChoiceOption[];
}

export interface SingleChoiceOption {
  order: number;
  id: number;
  text: string;
  isCorrect: boolean;
}

export interface LessonDetail {
  id: number;
  documentId: string;
  title: string;
  content: string;
  order: number;
  duration?: number;
  videoUrl?: string;
  isFree: boolean;
  slug: string;
  chapter: {
    id: number;
    documentId: string;
    title: string;
    description: string;
    order: number;
  };
  course: {
    id: number;
    documentId: string;
    title: string;
    description: string;
    difficultyLevel: string;
  };
  fragments: LessonFragment[];
  attachments?: any;
}

export interface LessonProgress {
  id: number;
  documentId: string;
  completed: boolean;
  progressPercentage: number;
  startedAt?: string;
  completedAt?: string;
  timeSpent?: number;
  lastAccessedAt?: string;
  lesson: Lesson;
  chapter: {
    id: number;
    documentId: string;
    title: string;
  };
  course: {
    id: number;
    documentId: string;
    title: string;
  };
}

export interface UpdateLessonProgressRequest {
  courseId: number;
  chapterId: number;
  lessonId: number;
  completed: boolean;
  progressPercentage: number;
  timeSpent: number;
}

export interface FragmentProgress {
  id: number;
  documentId: string;
  fragmentId: number;
  lessonId: number;
  chapterId: number;
  courseId: number;
  completed: boolean;
  progressPercentage: number;
  timeSpent?: number;
  lastAccessedAt?: string;
  startedAt?: string;
  completedAt?: string;
}

export interface UpdateFragmentProgressRequest {
  courseId: number;
  chapterId: number;
  lessonId: number;
  fragmentId: number;
  fragmentIndex: number; // Add fragment index for URL
  completed: boolean;
  progressPercentage: number;
  timeSpent: number;
}

export interface FragmentProgressResponse {
  success: boolean;
  fragmentProgress: FragmentProgress[];
}

export interface LessonProgressResponse {
  success: boolean;
  progress: LessonProgress[];
}

/**
 * Get lesson details by lesson ID
 */
export const getLessonById = async (lessonId: number): Promise<LessonDetail> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.get(`${SERVER_URL}/api/lessons/${lessonId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    const lessonData = response.data.data || response.data;
    return lessonData;
  } catch (error) {
    console.error('Error fetching lesson by ID:', error);
    throw error;
  }
};

/**
 * Update lesson progress
 */
export const updateLessonProgress = async (progressData: UpdateLessonProgressRequest): Promise<void> => {
  try {
    const token = localStorage.getItem('jwt_token');
    const response = await axios.post(`${SERVER_URL}/api/product-user/update-lesson-progress`, progressData, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to update lesson progress');
    }

    return response.data;
  } catch (error) {
    console.error('Error updating lesson progress:', error);
    throw error;
  }
};

/**
 * Get fragment progress for a specific lesson
 */
export const getFragmentProgress = async (lessonId: number): Promise<FragmentProgressResponse> => {
  try {
    const token = localStorage.getItem('jwt_token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    // Get current user to get user ID
    const userResponse = await axios.get(`${SERVER_URL}/api/product-user/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const userId = userResponse.data?.user?.id;
    if (!userId) {
      throw new Error('User ID not found in user data');
    }

    const response = await axios.get(
      `${SERVER_URL}/api/user-course-progress/lesson/${userId}/${lessonId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.data;
  } catch (error) {
    console.error('Error fetching fragment progress:', error);
    throw error;
  }
};

/**
 * Update fragment progress
 */
export const updateFragmentProgress = async (progressData: UpdateFragmentProgressRequest): Promise<any> => {
  try {
    const token = localStorage.getItem('jwt_token');

    if (!token) {
      throw new Error('Authentication token not found');
    }

    // Get current user to get user ID
    const userResponse = await axios.get(`${SERVER_URL}/api/product-user/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });

    const userId = userResponse.data?.user?.id;
    if (!userId) {
      throw new Error('User ID not found in user data');
    }

    const response = await axios.post(
      `${SERVER_URL}/api/user-course-progress/fragment/${userId}/${progressData.lessonId}/${progressData.fragmentIndex}`,
      progressData,
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }
    );

    if (response.status !== 200 && response.status !== 201) {
      throw new Error('Failed to update fragment progress');
    }

    return response.data;
  } catch (error) {
    console.error('Error updating fragment progress:', error);
    throw error;
  }
};

/**
 * Mark fragment as completed
 */
export const completeFragment = async (
  courseId: number,
  chapterId: number,
  lessonId: number,
  fragmentId: number,
  fragmentIndex: number,
  timeSpent: number = 0
): Promise<any> => {
  try {
    const progressData: UpdateFragmentProgressRequest = {
      courseId,
      chapterId,
      lessonId,
      fragmentId,
      fragmentIndex,
      completed: true,
      progressPercentage: 100,
      timeSpent,
    };

   return updateFragmentProgress(progressData);
  } catch (error) {
    console.error('Error completing fragment:', error);
    throw error;
  }
};

/**
 * Mark fragment as started/accessed
 */
export const startFragment = async (
  courseId: number,
  chapterId: number,
  lessonId: number,
  fragmentId: number,
  fragmentIndex: number
): Promise<void> => {
  try {
    const progressData: UpdateFragmentProgressRequest = {
      courseId,
      chapterId,
      lessonId,
      fragmentId,
      fragmentIndex,
      completed: false,
      progressPercentage: 0,
      timeSpent: 0,
    };

    await updateFragmentProgress(progressData);
  } catch (error) {
    console.error('Error starting fragment:', error);
    throw error;
  }
}

/**
 * Start lesson (mark as started)
 */
export const startLesson = async (
  courseId: number,
  chapterId: number,
  lessonId: number
): Promise<void> => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) throw new Error('No JWT token found');

    // Get userId from /api/product-user/me
    const userResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/product-user/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const userData = await userResponse.json();
    const userId = userData?.user?.id;
    if (!userId) throw new Error('User ID not found');

    // Call backend to start lesson
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/user-course-progress/start-lesson/${userId}/${lessonId}/${courseId}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      }
    );
    if (!response.ok) throw new Error('Failed to start lesson');
  } catch (error) {
    console.error('Error starting lesson:', error);
    throw error;
  }
};

/**
 * Finish lesson (mark as finished)
 */
export const finishLesson = async (
  courseId: number,
  chapterId: number,
  lessonId: number
): Promise<void> => {
  try {

    const token = localStorage.getItem('jwt_token');
    if (!token) throw new Error('No JWT token found');

    // Get userId from /api/product-user/me
    const userResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/product-user/me`, {
      headers: {Authorization: `Bearer ${token}`}
    });
    const userData = await userResponse.json();
    const userId = userData?.user?.id;
    if (!userId) throw new Error('User ID not found');

    // Call backend to finish lesson
    const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/user-course-progress/finish-lesson/${userId}/${lessonId}/${courseId}`,
        {
          method: 'POST',
          headers: {Authorization: `Bearer ${token}`}
        }
    );
    if (!response.ok) throw new Error('Failed to finish lesson');
  } catch (error) {
    console.error('Error finishing lesson:', error);
    throw error;
  }
}

  /**
   * Finish lesson (mark as finished)
   */
  export const evaluatePromptWithAi = async ({
      prompt, practiceTask
  }: { prompt: string, practiceTask: string  }
  ): Promise<{ evaluation: string}> => {
    try {
      const token = localStorage.getItem('jwt_token');
      if (!token) throw new Error('No JWT token found');

      // Call backend to finish lesson
      const response = await axios.post(
          `${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/openai/evaluate-prompt`,
          {
              criteria: [],
              model: 'gpt-3.5-turbo',
              lessonId: 0,
              fragmentIndex: 0,
              practiceId: '0',
              practiceTask,
              prompt
          },
          {
            headers: {Authorization: `Bearer ${token}`},
          }
      );
      return response.data;
    } catch (error) {
      console.error('Error with request to Open AI:', error);
      throw error;
    }
};

/**
 * Submit practice progress
 */
export const submitPracticeProgress = async (
    {
      lessonId,
      fragmentId,
      fragmentIndex,
      courseId,
      practiceId,
      practiceType,
      answer,
      isCorrect
    } : {
      courseId: string
      lessonId: any
      fragmentId: number
      fragmentIndex: number
      practiceId: number
      practiceType: string
      answer: string
      isCorrect: boolean
    }
): Promise<any> => {
  try {
    const token = localStorage.getItem('jwt_token');
    if (!token) throw new Error('No JWT token found');

    // Get userId from /api/product-user/me
    const userResponse = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/product-user/me`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const userData = await userResponse.json();
    const userId = userData?.user?.id;
    if (!userId) throw new Error('User ID not found');

    // Call backend to submit practice progress
    const response = await fetch(
      `${process.env.REACT_APP_API_URL || 'http://localhost:1337'}/api/user-course-progress/submit-practice/${userId}/${lessonId}/${fragmentIndex}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          practiceId,
          practiceType,
          answer,
          isCorrect,
          completed: true,
          courseId: courseId,
          fragmentId: fragmentId,
          fragmentIndex: fragmentIndex,
          lessonId: lessonId,
        })
      }
    );
    const result = await response.json();
    if (!response.ok) throw new Error('Failed to submit practice progress');
    return result.data;
  } catch (error) {
    console.error('Error submitting practice progress:', error);
    throw error;
  }
};

/**
 * Finish lesson (mark as finished)
 */
